import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { callsAPI } from '../services/api.js';
import { useEventStream } from '../hooks/useEventStream.js';
import CallCaptured from '../components/CallCaptured.jsx';
import {
  formatDateTime,
  formatDateTimeWithZone,
  formatDuration,
  formatPhone,
  statusBadge
} from '../lib/format.js';

export default function CallDetail() {
  const { id } = useParams();
  const [call, setCall] = useState(null);
  const [conversation, setConversation] = useState([]);
  // The READABLE projection core builds from the same rows (control tokens stripped,
  // [Name] tags hoisted into the speaker, injected directives dropped, STT fragments
  // coalesced, delivery marks kept). Falls back to the raw rows when absent.
  const [displayTurns, setDisplayTurns] = useState([]);
  // Which transcript to show. "verbatim" = re-derived FROM THE AUDIO (what was said);
  // "intent" = assembled from the turn log (what the AI generated / STT first heard).
  const [view, setView] = useState('intent');
  const [liveAppended, setLiveAppended] = useState(false);
  const [verbatimBusy, setVerbatimBusy] = useState(false);
  const [verbatimError, setVerbatimError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // The recording is served by an AUTH-GATED core route, so a bare <audio src>
  // can't fetch it (no bearer header) — it would 401 silently. Fetch it with auth
  // via recordingBlobUrl into an object URL the player can use.
  const [recording, setRecording] = useState(null); // { url, mimeType, ext }
  const [recordingErr, setRecordingErr] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const callData = await callsAPI.get(id);
      // Normalise the wrapper if core/ returns one.
      const row = callData?.data || callData;
      setCall(row);
      if (row?.transcriptVerbatim) setView('verbatim');
      const inlineDisplay = callData?.displayTurns || callData?.data?.displayTurns;
      setDisplayTurns(Array.isArray(inlineDisplay) ? inlineDisplay : []);
      setLiveAppended(false);

      // Conversation may come back nested in the call payload OR via a
      // dedicated endpoint — try both.
      const inline = callData?.conversations || callData?.data?.conversations;
      if (Array.isArray(inline) && inline.length) {
        setConversation(inline);
      } else {
        try {
          const conv = await callsAPI.conversation(id);
          const list =
            Array.isArray(conv) ? conv : conv?.conversation || conv?.data || [];
          setConversation(list);
          if (Array.isArray(conv?.displayTurns) && !inlineDisplay) setDisplayTurns(conv.displayTurns);
        } catch {
          setConversation([]);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load call.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch the recording (with auth) whenever the call's recordingUrl changes, and
  // revoke the object URL on cleanup so we don't leak blobs when navigating away.
  useEffect(() => {
    const src = call?.recordingUrl;
    if (!src) {
      setRecording(null);
      setRecordingErr('');
      return;
    }
    let objectUrl = null;
    let cancelled = false;
    setRecordingErr('');
    callsAPI
      .recordingBlobUrl(src)
      .then((rec) => {
        if (cancelled) {
          if (rec?.url) URL.revokeObjectURL(rec.url);
          return;
        }
        objectUrl = rec?.url || null;
        setRecording(rec);
      })
      .catch((err) => {
        if (!cancelled) setRecordingErr(err.message || 'Could not load the recording.');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [call?.recordingUrl]);
  const buildVerbatim = async () => {
    setVerbatimBusy(true);
    setVerbatimError('');
    try {
      const res = await callsAPI.buildVerbatim(id);
      const data = res?.data || res;
      setCall((prev) => ({ ...(prev || {}), transcriptVerbatim: data?.transcriptVerbatim, transcriptVerbatimMeta: data?.transcriptVerbatimMeta }));
      setView('verbatim');
    } catch (err) {
      setVerbatimError(err.message || 'Could not transcribe the recording.');
    } finally {
      setVerbatimBusy(false);
    }
  };

  // Live-appended rows carry no display projection; while a call is in progress the
  // raw rows are the only complete list, so show those until the next full load.
  const readable = displayTurns.length > 0 && !liveAppended ? displayTurns : null;
  const missingAi = call?.transcriptVerbatimMeta?.aiTurns?.missing || [];

  // While the call is in progress, append transcript fragments as they arrive.
  useEventStream({
    transcript_appended: (payload) => {
      if (!payload || payload.callId !== id) return;
      setConversation((prev) => [...prev, payload]);
      setLiveAppended(true);
    },
    call_ended: (payload) => {
      if (payload?.callId === id) load();
    }
  });

  return (
    <div className="px-6 md:px-10 py-8 max-w-4xl">
      <Link
        to="/calls"
        className="inline-flex items-center gap-1 text-sm text-stone-400 hover:text-alfred-gold mb-4"
      >
        <ChevronLeft size={16} />
        Back to calls
      </Link>

      {/* Zone-labelled: this page is where someone reconstructs when a call
          actually happened, so the clock it's stated on has to be explicit. */}
      <PageHeader
        title="Call detail"
        subtitle={call ? formatDateTimeWithZone(call.startedAt) : null}
      />

      {error && (
        <div className="mb-6">
          <ErrorBanner message={error} />
        </div>
      )}

      {loading && !call ? (
        <div className="card text-stone-500">Loading…</div>
      ) : call ? (
        <>
          <div className="card mb-6">
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-stone-500">
                  From
                </dt>
                <dd className="mt-1 text-stone-200">
                  {formatPhone(call.fromNumber)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-stone-500">
                  To
                </dt>
                <dd className="mt-1 text-stone-200">
                  {formatPhone(call.toNumber)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-stone-500">
                  Duration
                </dt>
                <dd className="mt-1 text-stone-200 tabular-nums">
                  {formatDuration(call.durationSeconds)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-stone-500">
                  Status
                </dt>
                <dd className="mt-1">
                  <span className={`badge ${statusBadge(call.status)}`}>
                    {call.status || 'unknown'}
                  </span>
                </dd>
              </div>
            </dl>
            {call.direction && (
              <p className="mt-4 text-xs text-stone-500">
                Direction: <span className="text-stone-300">{call.direction}</span>
              </p>
            )}
          </div>

          {call.recordingUrl && (
            <div className="card mb-6">
              <h2 className="text-sm uppercase tracking-wider text-stone-500 mb-3">
                Recording
              </h2>
              {recordingErr ? (
                <p className="text-sm text-rose-400">{recordingErr}</p>
              ) : !recording ? (
                <p className="text-sm text-stone-500">Loading recording…</p>
              ) : (
                <>
                  {/* src is an authenticated object URL (recordingBlobUrl), not the
                      raw auth-gated path — a bare <audio src> to that path 401s. */}
                  <audio controls src={recording.url} className="w-full">
                    Your browser does not support audio playback.
                  </audio>
                  <p className="mt-2 text-xs text-stone-500">
                    <a
                      href={recording.url}
                      download={`recording-${id}${recording.ext ? `.${recording.ext}` : ''}`}
                      className="hover:text-alfred-gold underline"
                    >
                      Download recording
                    </a>
                  </p>
                </>
              )}
            </div>
          )}

          {/* Above the transcript on purpose: the captured detail is what someone
              opened this page for, and it was previously nowhere at all. */}
          <CallCaptured call={call} />

          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h2 className="text-xl text-stone-100">Transcript</h2>
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setView('intent')}
                className={`px-3 py-1 rounded border ${view === 'intent' ? 'border-alfred-gold text-alfred-gold' : 'border-stone-700 text-stone-400 hover:text-stone-200'}`}
                title="Assembled live from the turn log: what the AI generated and what streaming speech-to-text first heard."
              >
                From the call log
              </button>
              {call?.transcriptVerbatim ? (
                <button
                  type="button"
                  onClick={() => setView('verbatim')}
                  className={`px-3 py-1 rounded border ${view === 'verbatim' ? 'border-alfred-gold text-alfred-gold' : 'border-stone-700 text-stone-400 hover:text-stone-200'}`}
                  title="Re-transcribed from the recording after the call: what was actually said."
                >
                  From the recording
                </button>
              ) : call?.recordingUrl ? (
                <button
                  type="button"
                  onClick={buildVerbatim}
                  disabled={verbatimBusy}
                  className="px-3 py-1 rounded border border-stone-700 text-stone-400 hover:text-stone-200 disabled:opacity-50"
                  title="Transcribe the recording now (a few seconds; about half a cent per minute of audio)."
                >
                  {verbatimBusy ? 'Transcribing…' : 'Transcribe the recording'}
                </button>
              ) : null}
            </div>
          </div>
          {verbatimError && <p className="mb-3 text-xs text-red-400">{verbatimError}</p>}
          <div className="card p-0 overflow-hidden">
            {view === 'verbatim' && call?.transcriptVerbatim ? (
              <div>
                <pre className="px-5 py-4 text-sm text-stone-200 whitespace-pre-wrap break-words font-sans leading-relaxed">
                  {call.transcriptVerbatim}
                </pre>
                {missingAi.length > 0 && (
                  <div className="px-5 py-3 border-t border-stone-900 text-xs text-stone-400">
                    <p className="uppercase tracking-wider text-stone-500 mb-1">
                      Generated by the AI but not found in the recording
                    </p>
                    <ul className="space-y-1">
                      {missingAi.map((m) => (
                        <li key={m.index} className="line-through decoration-stone-600">{m.text}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {call?.transcriptVerbatimMeta && (
                  <p className="px-5 py-2 border-t border-stone-900 text-[11px] text-stone-600">
                    {call.transcriptVerbatimMeta.provider}/{call.transcriptVerbatimMeta.model}
                    {call.transcriptVerbatimAt ? ` · ${formatDateTime(call.transcriptVerbatimAt)}` : ''}
                    {typeof call.transcriptVerbatimMeta.aiTurns?.found === 'number'
                      ? ` · ${call.transcriptVerbatimMeta.aiTurns.found}/${call.transcriptVerbatimMeta.aiTurns.total} AI lines heard in the audio`
                      : ''}
                  </p>
                )}
              </div>
            ) : readable ? (
              <ul className="divide-y divide-stone-900">
                {readable.map((turn, i) => (
                  <li
                    key={turn.id || i}
                    className={`px-5 py-4 flex gap-4 items-start ${turn.delivered === false ? 'opacity-60' : ''}`}
                  >
                    <span
                      className={`badge shrink-0 mt-0.5 ${
                        turn.role === 'assistant'
                          ? 'bg-alfred-gold/15 text-alfred-gold border border-alfred-gold/30'
                          : 'bg-stone-800 text-stone-200 border border-stone-700'
                      }`}
                    >
                      {turn.speaker || turn.role || 'turn'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm text-stone-200 whitespace-pre-wrap break-words ${turn.delivered === false ? 'line-through decoration-stone-500' : ''}`}>
                        {turn.text}
                      </p>
                      {turn.delivered === false && (
                        <p className="mt-1 text-xs text-amber-500/80">
                          Not spoken — {turn.undeliveredReason || 'unknown'}
                        </p>
                      )}
                      {turn.cutOff && (
                        <p className="mt-1 text-xs text-amber-500/80">Cut off — {turn.cutOff}</p>
                      )}
                      {turn.ts && (
                        <p className="mt-1 text-xs text-stone-500">
                          {formatDateTime(turn.ts)}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : conversation.length === 0 ? (
              <div className="p-6 text-sm text-stone-500">
                No transcript available for this call.
              </div>
            ) : (
              <ul className="divide-y divide-stone-900">
                {conversation.map((turn, i) => (
                  <li
                    key={turn.id || i}
                    className="px-5 py-4 flex gap-4 items-start"
                  >
                    <span
                      className={`badge shrink-0 mt-0.5 ${
                        turn.role === 'assistant'
                          ? 'bg-alfred-gold/15 text-alfred-gold border border-alfred-gold/30'
                          : turn.role === 'system'
                            ? 'bg-stone-800 text-stone-400 border border-stone-700'
                            : 'bg-stone-800 text-stone-200 border border-stone-700'
                      }`}
                    >
                      {turn.role || 'turn'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-stone-200 whitespace-pre-wrap break-words">
                        {turn.content}
                      </p>
                      {turn.ts && (
                        <p className="mt-1 text-xs text-stone-500">
                          {formatDateTime(turn.ts)}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : (
        <div className="card text-stone-500">Call not found.</div>
      )}
    </div>
  );
}
